import React, { useEffect, useState, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { Flex, Form, message, Spin } from 'antd';
import Modal from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import { ArrowUpOutlined } from '@ant-design/icons';
import Filter from 'admin/components/Filter';
import { Input, Button, TextArea } from 'ui';
import { StyledCategoryAdmin } from './style';
import { CategoryData } from 'types/CategoryData';
import useQueryApiClient from 'utils/useQueryApiClient';
import { DroppableArea } from './DroppableArea';
import { DraggableCategory } from './DraggableCategory';
import useScroll from 'utils/useScroll';

type AdvancedSearchState = Record<string, unknown>;

const CategoryTab = () => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const [advancedSearch, setAdvancedSearch] = useState<AdvancedSearchState>({});
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [previousCategories, setPreviousCategories] = useState<CategoryData[]>(
    []
  );
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeData, setActiveData] = useState<CategoryData | null>(null);
  const [hoverPosition, setHoverPosition] = useState<'top' | 'bottom' | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showScroll, setShowScroll] = useState(false);
  const [isSavingSort, setIsSavingSort] = useState(false);
  const { scrollToTop } = useScroll();

  const { refetch, isLoading: isLoadingCategories } = useQueryApiClient({
    request: {
      url: '/api/v2/categories/menu/desktop',
      data: { filter: { ...advancedSearch }, isUpdated: true },
      disableOnMount: true,
    },
    onSuccess: (response: unknown) => {
      setCategories(Array.isArray(response) ? response : []);
    },
  });

  useEffect(() => {
    refetch();
  }, [advancedSearch]);

  const { appendData: saveCategorySort } = useQueryApiClient({
    request: {
      url: '/api/v2/category/sort',
      method: 'POST',
    },
    onSuccess: (newCategory: Partial<CategoryData>) => {
      const safeCategory = {
        ...newCategory,
        subcategories: Array.isArray(newCategory.subcategories)
          ? newCategory.subcategories
          : [],
      };
      setCategories((prev) => [...prev, safeCategory as CategoryData]);
      refetch();
    },
    onError: () => {
      setIsSavingSort(false);
      message.error(intl.formatMessage({ id: 'message.error_save_category' }));
    },
  });

  const { appendData: deleteCategory } = useQueryApiClient({
    request: {
      url: '/api/v2/category/:id',
      method: 'DELETE',
    },
    onSuccess: () => {
      setIsModalOpen(false);
      refetch();
    },
    onError: (e) => {
      if (e?.error) {
        message.error(intl.formatMessage({ id: e?.error }));
      }
    },
  });

  const buildCategoryTree = (
    categories: CategoryData[]
  ): { id: number; children: any[] }[] => {
    return categories.map((category) => ({
      id: category.id,
      children: category.subcategories
        ? buildCategoryTree(category.subcategories)
        : [],
    }));
  };

  const saveSortedCategories = (sorted: CategoryData[]) => {
    const tree = buildCategoryTree(sorted);
    saveCategorySort({ list: JSON.stringify(tree) });
  };

  const { appendData: updateCategory, isLoading } = useQueryApiClient({
    request: {
      url: `/api/v2/category/${activeData?.id}`,
      method: 'PATCH',
    },
    onSuccess: (response: Partial<CategoryData>) => {
      message.success(
        intl.formatMessage({ id: 'message.success_category_update' })
      );
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === activeData?.id ? { ...cat, ...response } : cat
        )
      );
      setIsModalOpen(false);
      refetch();
    },
    onError: () => {
      message.error(
        intl.formatMessage({ id: 'message.failed_category_update' })
      );
    },
  });

  const { appendData: createCategory } = useQueryApiClient({
    request: {
      url: '/api/v2/category',
      method: 'POST',
    },
    onSuccess: (newCategory: CategoryData) => {
      setCategories((prev) => [...prev, newCategory]);
      refetch();
    },
    onError: () => {},
  });

  const handleEditCategory = useCallback(
    (category: CategoryData) => {
      form.setFieldsValue({
        name: category.name,
        vat: category.vat,
        description: category.description,
      });
      setActiveData(category);
      setIsModalOpen(true);
    },
    [form]
  );

  const handleSave = useCallback(() => {
    form.validateFields().then((values) => {
      updateCategory(values);
    });
  }, [form, updateCategory]);

  const handleCreateCategory = useCallback(
    (values: { categoryName: string }) => {
      createCategory({ name: values.categoryName });
    },
    [createCategory]
  );

  const checkScrollTop = useCallback(() => {
    setShowScroll(window.scrollY > 400);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', checkScrollTop);

    return () => window.removeEventListener('scroll', checkScrollTop);
  }, [checkScrollTop]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveData(null);

    if (!over || active.id === over.id || !activeData) {
      setCategories(previousCategories);

      return;
    }

    const overId = over?.id?.toString() ?? null;
    const overNumericId = Number(overId);
    if (!overId || isNaN(overNumericId)) {
      setCategories(previousCategories);

      return;
    }

    const newTree = structuredClone(categories);

    const findAndRemove = (
      list: CategoryData[],
      id: number
    ): [CategoryData | null, CategoryData[]] => {
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        if (item.id === id) {
          list.splice(i, 1);

          return [item, list];
        }
        if (item.subcategories) {
          const [removed, updated] = findAndRemove(item.subcategories, id);
          if (removed) {
            item.subcategories = updated;

            return [removed, list];
          }
        }
      }
      return [null, list];
    };

    const insertRelative = (
      list: CategoryData[],
      targetId: number,
      dragged: CategoryData
    ): boolean => {
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        if (item.id === targetId) {
          const activeLeft = active.rect.current?.translated?.left ?? 0;
          const overLeft = over.rect.left;
          const deltaX = activeLeft - overLeft;

          const activeTop = active.rect.current?.translated?.top ?? 0;
          const overTop = over.rect.top;
          const midpoint = overTop + over.rect.height / 2;

          if (deltaX > 30) {
            item.subcategories = item.subcategories || [];
            item.subcategories.push(dragged);
          } else {
            activeTop < midpoint
              ? list.splice(i, 0, dragged)
              : list.splice(i + 1, 0, dragged);
          }

          return true;
        }

        if (item.subcategories) {
          const inserted = insertRelative(
            item.subcategories,
            targetId,
            dragged
          );
          if (inserted) {
            return true;
          }
        }
      }
      return false;
    };

    const [removedItem, cleanTree] = findAndRemove(newTree, activeData.id);
    if (!removedItem) {
      setCategories(previousCategories);

      return;
    }

    const wasInserted = insertRelative(cleanTree, overNumericId, removedItem);
    wasInserted ? setCategories(cleanTree) : setCategories(previousCategories);
    if (wasInserted) {
      saveSortedCategories(cleanTree);
    }
  };

  return (
    <>
      <Filter
        onFinish={(filterValues: Record<string, unknown>) =>
          setAdvancedSearch(filterValues)
        }
        isPagination={false}
      >
        <Input
          label={intl.formatMessage({ id: 'general.search' })}
          name="search"
        />
        <Button
          type="primary"
          htmlType="submit"
          label={intl.formatMessage({ id: 'general.select' })}
        />
      </Filter>

      <DndContext
        onDragStart={({ active }) => {
          setActiveId(active.id);
          if (active.data.current?.category) {
            setActiveData(JSON.parse(active.data.current.category));
            setPreviousCategories(structuredClone(categories));
          }
        }}
        onDragOver={({ active, over }) => {
          if (!over?.rect || !active.rect?.current?.translated) {
            return;
          }
          const activeMidY =
            active.rect.current.translated.top +
            active.rect.current.translated.height / 2;
          const overMid = over.rect.top + over.rect.height / 2;
          setHoverPosition(activeMidY < overMid ? 'top' : 'bottom');
        }}
        onDragEnd={handleDragEnd}
      >
        <Spin spinning={isSavingSort}>
          <StyledCategoryAdmin>
            {showScroll && (
              <Button
                type="primary"
                icon={<ArrowUpOutlined />}
                onClick={scrollToTop}
                className="scrolltop"
              />
            )}

            <div className="add-new">
              <Form onFinish={handleCreateCategory}>
                <Input type="text" required name="categoryName" />
                <Button
                  type="primary"
                  htmlType="submit"
                  label={intl.formatMessage({ id: 'general.add' })}
                />
              </Form>
            </div>

            <Spin spinning={isLoading || isLoadingCategories}>
              {categories.map((category) => (
                <DroppableArea
                  key={category.id}
                  category={category}
                  onDrop={handleDragEnd}
                  onEdit={handleEditCategory}
                  hoverPosition={hoverPosition}
                  activeId={activeId}
                />
              ))}
            </Spin>

            <DragOverlay>
              {activeId && activeData && (
                <DraggableCategory
                  category={activeData}
                  isDragging={true}
                  onEdit={handleEditCategory}
                />
              )}
            </DragOverlay>
          </StyledCategoryAdmin>
        </Spin>
      </DndContext>

      <Modal
        title={intl.formatMessage({ id: 'category.edit_category' })}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSave}
        confirmLoading={isLoading}
        footer={
          <Flex gap={10} justify="end">
            <Button
              label={intl.formatMessage({ id: 'general.delete' })}
              onClick={() => deleteCategory([], { id: activeData?.id })}
              loading={isLoading}
              className="delete-button"
            />
            <Button
              label={intl.formatMessage({ id: 'general.back' })}
              onClick={() => setIsModalOpen(false)}
              loading={isLoading}
            />
            <Button
              label={intl.formatMessage({ id: 'general.submit' })}
              type="primary"
              htmlType="submit"
              onClick={handleSave}
              loading={isLoading}
            />
          </Flex>
        }
      >
        <Form form={form} layout="vertical">
          <Input
            name="name"
            label={intl.formatMessage({ id: 'category.category_name' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'error.needs_name' }),
              },
            ]}
          />
          <Input
            type="number"
            name="vat"
            label={intl.formatMessage({ id: 'category.vat' })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'error.needs_vat' }),
              },
            ]}
          />
          <TextArea
            name="description"
            label={intl.formatMessage({ id: 'qmark.description' })}
          />
        </Form>
      </Modal>
    </>
  );
};

export default CategoryTab;
